import { useMutation } from "@tanstack/react-query";
import { IndexPath, Select, SelectItem, Text } from "@ui-kitten/components";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import Button from "components/Button";
import Input from "components/Input";
import Spacer from "components/Spacer";
import {
  EventAvailabilityDetails,
  EventDetails,
  GroupTicketDetails,
} from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppSelector } from "hooks/useAppSelector";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import {
  transferTicketFetcher,
  useGetTransferDetailsQuery,
} from "queries/ticket.queries";
import React, { useEffect, useState } from "react";
import { View } from "react-native";
import TDCDialog from "screens/Schedule/components/TDCDialog";
import accountServices from "services/account.services";
import { toDashboard } from "utils/navigationUtils";
import toast from "utils/toast";

interface StepTwoProps {
  transferCode: string;
  eventCode: string;
  type: "guest" | "voucher";
  event: EventDetails;
}
const StepTwo: React.FC<StepTwoProps> = (props) => {
  const { transferCode, eventCode, type, event } = props;
  const [selectedIndex, setSelectedIndex] = useState<IndexPath>(
    new IndexPath(0)
  );
  const [TDCDialogVisible, setTDCDialogVisible] = useState(false);
  const userId = useAppSelector((state) => state.auth.id);

  const { account, updateFetchAccountBffCache } = useAccount();
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const {
    data: transferDetails,
    error,
    refetch: refetchTransferDetails,
  } = useGetTransferDetailsQuery({
    eventCode,
    transferCode,
  });
  const { mutateAsync: createAccount, isLoading: creatingAccount } =
    useMutation(accountServices.createAccount);
  const { mutateAsync: transfer, isLoading } = useMutation(
    transferTicketFetcher
  );
  const handleOnSelect = (index: IndexPath) => {
    setSelectedIndex(index);
  };

  const handleSubmit = async () => {
    // Remember, that if TicketProvider == TDC, then the user MUST go through the TDC account linking process if it has not yet been completed!
    if (
      event.barcodeProvider === "TDC" &&
      account?.extUserDetails.tdcUsername === null
    ) {
      setTDCDialogVisible(true);
      return;
    }
    await transfer({
      eventCode,
      transferCode,
      quantity: selectedIndex.row + 1,
    });
    toast.success("You have successfully claimed your seat");
    toDashboard();
  };

  useEffect(() => {
    if (!transferDetails) return;
    setSelectedIndex(new IndexPath(transferDetails.remainingClaimable - 1));
  }, [transferDetails]);

  // If user does not have a subscription for current tenant(but does for another tenant), register them as associate account
  if (error?.data.code === "ACCOUNT_NOT_FOUND" && !creatingAccount) {
    createAccount({
      type: "ASSOCIATE",
      primaryUser: {
        userId: userId!,
        createMemberPlanPackageParams: [],
      },
      source: "FANRALLY_APP",
    }).then((account) => {
      updateFetchAccountBffCache(account);
      refetchTransferDetails();
    });
  }

  return (
    <View style={{ marginVertical: 15 }}>
      <Input
        label={
          type === "guest" ? "Your Guest Invitation Code" : "Your Voucher Code"
        }
        value={transferCode}
      ></Input>

      {transferDetails !== undefined && (
        <View>
          <View style={{ marginVertical: 15 }}>
            {transferDetails.remainingClaimable > 0 ? (
              <>
                <View>
                  <Select
                    label={`How many seats would you like to claim? (Maximum ${transferDetails.remainingClaimable})`}
                    onSelect={(index) => handleOnSelect(index as IndexPath)}
                    selectedIndex={selectedIndex}
                    value={selectedIndex.row + 1}
                  >
                    {new Array(transferDetails.remainingClaimable)
                      .fill(0)
                      .map((_, index) => (
                        <SelectItem key={index} title={index + 1}></SelectItem>
                      ))}
                  </Select>
                </View>
                <Spacer />
                <Button onPress={handleSubmit} loading={isLoading}>
                  Claim
                </Button>

                <BaseCard title="Attendees" style={{ marginVertical: 15 }}>
                  {transferDetails.groupCode.tickets
                    .filter((t) => t.status === "RESERVED")
                    .map((ticket) => {
                      const resolveTitle = (ticket: GroupTicketDetails) => {
                        if (
                          ticket.accountId === ticket.reservedByAccountId &&
                          ticket.planPackageComponentType.includes("GUEST")
                        ) {
                          return "Guest Pending";
                        } else {
                          return `${ticket.firstName} ${ticket.lastName}`;
                        }
                      };

                      return (
                        <BaseCardListItem
                          key={ticket.id}
                          title={resolveTitle(ticket)}
                          showArrow={false}
                        />
                      );
                    })}
                </BaseCard>
              </>
            ) : (
              <Text status="danger">
                All of the guest passes for this reservation have already been
                claimed
              </Text>
            )}
          </View>

          {TDCDialogVisible && (
            <TDCDialog
              visible={TDCDialogVisible}
              setVisible={setTDCDialogVisible}
              event={event as unknown as EventAvailabilityDetails}
            />
          )}
        </View>
      )}
    </View>
  );
};

export default StepTwo;
